import '@/assets/styles/views/solver/white-board.scss';
import WhiteBoardWv from '@/components/meet-camera/whiteboard-wv';
import { SolutionVideoModal, SolutionVideoModalV2 } from '@/screens/solver/_components';
import { useRef, useState, useCallback, Fragment, useEffect } from 'react';
import { MediaRecorderManager } from '@/managers/media-recorder-manager.tsx';
import { useLocation, useNavigate, useBeforeUnload, useBlocker } from 'react-router';
import { Modal, DialogModal } from '@/components';
import { toast } from 'react-toastify';
import { NavigationPath } from '@/router/navigation-path';
import DOMPurify from 'dompurify';
import { useSolverTimer, formatTime } from '@/hooks/use-solver-timer';
import { LocationState, RecordingState } from '@/screens/solver/white-board/types.ts';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { PassedQuestionCreateRequest } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import ExceptionManager from '@/managers/exception-manager.tsx';
import { useApi } from '@/api/useApi';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useQueryClient } from 'react-query';
import { TimeSpan } from '@/utility/time-span.ts';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { isWebView, sendMessageToWebView } from '@/utility/webview.ts';
import { PostMessage } from '@/constants/post-message.ts';
import { useFeatureFlag } from 'configcat-react';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';

const MAX_RECORDING_DURATION = 2400;
const WARNING_TIME_THRESHOLD = 180;
const VIDEO_QUALITY = 'low';
const VIDEO_FRAME_RATE = 24;
const NOTE_TRUNCATE_LENGTH = 42;

const passSchema = yup.object({
	passQuestion: yup.string().required('Lütfen bir iptal nedeni seçin')
});

type PassFormData = {
	passQuestion: string;
};

const SolverWhiteBoard: React.FC = () => {
	const { value: isActiveSolverVP8Codec } = useFeatureFlag(CONFIGCAT_KEYS.IS_ACTIVE_SOLVER_VP8_CODEC, false);

	const { value: isActiveSolverV2UploadScreen } = useFeatureFlag(CONFIGCAT_KEYS.IS_ACTIVE_SOLVER_V2_UPLOAD_SCREEN, false);

	const queryClient = useQueryClient();

	const location = useLocation();
	const navigate = useNavigate();
	const locationState = location.state as LocationState;

	const { SolverQuestionControllerApi } = useApi();

	const interactionId = locationState?.interactionId;
	const solverTimer = useSolverTimer(interactionId);

	const whiteboardRef = useRef<any>();
	const recorderRef = useRef<MediaRecorderManager | null>(null);
	const hasStartedRef = useRef(false);

	const [recordingState, setRecordingState] = useState<RecordingState>({
		isRecording: false,
		isPaused: false,
		currentTime: 0
	});

	const [showTimeoutModal, setShowTimeoutModal] = useState(false);
	const [showQuestionInfo, setShowQuestionInfo] = useState(true);
	const [showQuestionNoteModal, setShowQuestionNoteModal] = useState(false);
	const [showSolutionVideoModal, setShowSolutionVideoModal] = useState(false);
	const [showDeleteVideoModal, setShowDeleteVideoModal] = useState(false);
	const [showQuestionImageOnWhiteBoard, setShowQuestionImageOnWhiteBoard] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
		reset
	} = useForm<PassFormData>({
		resolver: yupResolver(passSchema),
		mode: 'onChange'
	});

	const { data: solverPassQuestion } = useQueryProvider([QUERY_KEYS.solverPassQuestion], () => SolverQuestionControllerApi.getPassReasons(), {
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		enabled: showDeleteVideoModal
	});

	const createPassedQuestion = useMutationProvider((body: PassedQuestionCreateRequest) => SolverQuestionControllerApi.createPassedQuestion(body), {
		onError: error => {
			solverTimer.clearTimer();
			setTimeout(() => {
				setShowDeleteVideoModal(false);
				navigate(NavigationPath.solverSolve);
			}, TimeSpan.milliSecond.threeHundred);
			ExceptionManager.errorHandler(error);
		},
		onSuccess: () => {
			queryClient.refetchQueries({
				queryKey: [QUERY_KEYS.getRemainingPassedQuestionCount]
			});
			toast.error('Günde 10 soru iptal etme hakkınız bulunmaktadır. Daha fazla soru çözebilmek için haklarınızı dikkatli kullanınız.');
		}
	});

	useEffect(() => {
		window.handleWebViewMessage = (message: any) => {
			if (message.type === PostMessage.VIDEO_SENT_SUCCESS) {
				navigate(NavigationPath.solverSolve);
				queryClient.refetchQueries([QUERY_KEYS.getExpertQuestionPool]);
				toast.success('Video çözümün başarıyla gönderildi.');
			}

			if (message.type === PostMessage.RECORDING_COMPLETED) {
				handleRecordingCompleted(message.data);
			}

			if (message.type === PostMessage.APP_IS_INACTIVE) {
				navigate(NavigationPath.solverSolve);
			}

			if (message.type === PostMessage.onClearClick) {
				handleClearClick();
			}
		};

		window.showSolutionVideoModal = (data: any) => {
			handleRecordingCompleted(data);
		};

		window.handleCloseWhiteBoard = () => {
			handleCloseWhiteBoard();
		};

		const handleRecordingCompletedEvent = (event: CustomEvent) => {
			handleRecordingCompleted(event.detail);
		};

		window.addEventListener('recordingCompleted', handleRecordingCompletedEvent);

		return () => {
			delete window.handleWebViewMessage;
			delete window.showSolutionVideoModal;
			delete window.handleCloseWhiteBoard;
			window.removeEventListener('recordingCompleted', handleRecordingCompletedEvent);
		};
	}, []);

	const handleRecordingCompleted = (data: any) => {
		let recordedData = null;

		if (data?.recordedData) {
			recordedData = {
				url: data.recordedData.base64 || data.recordedData.url,
				originalUrl: data.recordedData.url,
				base64: data.recordedData.base64,
				duration: data.recordedData.duration || 0,
				size: data.recordedData.size || 0,
				mimeType: data.recordedData.mimeType || 'video/mp4'
			};
		} else if (data?.videoUrl || data?.url) {
			recordedData = {
				url: data.videoUrl || data.url,
				duration: data.duration || 0,
				size: data.size || 0,
				mimeType: data.mimeType || 'video/mp4'
			};
		} else {
			toast.error('Video yüklenemedi. Lütfen tekrar video çekin.');

			setShowSolutionVideoModal(true);
			return;
		}
		setRecordingState(prev => {
			const newState = {
				...prev,
				isRecording: false,
				isPaused: false,
				recordedData: recordedData
			};
			return newState;
		});

		setShowSolutionVideoModal(true);
	};

	const truncateText = (text: string, maxLength: number = NOTE_TRUNCATE_LENGTH): string => {
		if (text.length <= maxLength) return text;
		return text.substring(0, maxLength) + '...';
	};

	const purify = (dirty: string) => ({
		__html: DOMPurify.sanitize(dirty)
	});

	useEffect(() => {
		// resolve mode'daki sorular gerçekten timeoutsuz atanıyor (backend'de timeoutAt yok),
		// bu yüzden bu ekranın "timer aktif değilse süre dolmuştur" varsayımı burada geçerli değil.
		if (locationState?.isResolveMode) return;

		if (!solverTimer.isActive) {
			if (recordingState.isRecording) {
				stopRecording();
			}
			setShowQuestionNoteModal(false);
			setShowSolutionVideoModal(false);
			setShowQuestionInfo(false);
			setShowDeleteVideoModal(false);
			setShowTimeoutModal(true);
		} else {
			if (showTimeoutModal) {
				setShowTimeoutModal(false);
			}
		}
	}, [solverTimer, locationState?.isResolveMode]);

	useEffect(() => {
		if (locationState?.isResolveMode) return;

		if (solverTimer.remainingTime === 0 && solverTimer.isActive === false && hasStartedRef.current) {
			if (recordingState.isRecording) {
				stopRecording();
			} else {
				setShowSolutionVideoModal(true);
			}
		}
	}, [solverTimer.remainingTime, solverTimer.isActive, recordingState.isRecording, locationState?.isResolveMode]);

	useBeforeUnload(
		useCallback(
			(e: BeforeUnloadEvent) => {
				if (recordingState.isRecording) {
					e.preventDefault();
					return 'Video kaydı devam ediyor!';
				}
				return false;
			},
			[recordingState.isRecording]
		)
	);

	useBlocker(
		useCallback(() => {
			if (recordingState.isRecording) {
				setShowDeleteVideoModal(true);
				return true;
			}
			return false;
		}, [recordingState.isRecording])
	);

	const getRecorder = useCallback(() => {
		if (!recorderRef.current) {
			const mimeType = isActiveSolverVP8Codec ? 'video/webm;codecs=vp8,opus' : 'video/mp4';
			recorderRef.current = new MediaRecorderManager({
				maxDuration: MAX_RECORDING_DURATION,
				videoQuality: VIDEO_QUALITY,
				audioBitrate: 96000,
				videoBitrate: 750000,
				audioEnabled: true,
				mimeType: mimeType,
				frameRate: VIDEO_FRAME_RATE,
				autoDownload: false,
				captureScreen: true,
				onRecordingStarted: () => {
					setRecordingState(prev => ({
						...prev,
						isRecording: true,
						isPaused: false,
						error: undefined
					}));
				},
				onRecordingStopped: data => {
					setRecordingState(prev => ({
						...prev,
						isRecording: false,
						isPaused: false,
						recordedData: data
					}));
				},
				onRecordingPaused: () => {
					setRecordingState(prev => ({ ...prev, isPaused: true }));
				},
				onRecordingResumed: () => {
					setRecordingState(prev => ({ ...prev, isPaused: false }));
				},
				onTimeUpdated: ({ currentTime }: { currentTime: number }) => {
					setRecordingState(prev => ({
						...prev,
						currentTime
					}));
				},
				onMaxDurationReached: () => {
					toast.warning('Maksimum kayıt süresi doldu!');
					setTimeout(() => {
						setShowSolutionVideoModal(true);
					}, 100);
				},
				onError: ({ message }: { message: string }) => {
					setRecordingState(prev => ({
						...prev,
						error: message,
						isRecording: false,
						isPaused: false
					}));
					toast.error(message);
				}
			});
		}
		return recorderRef.current;
	}, []);

	const startRecording = useCallback(async () => {
		if (recorderRef.current) {
			recorderRef.current.destroy();
			recorderRef.current = null;
		}

		setRecordingState({
			isRecording: false,
			isPaused: false,
			currentTime: 0,
			recordedData: undefined
		});

		const recorder = getRecorder();
		try {
			const success = await recorder.startRecording();
			if (success) {
				if (!hasStartedRef.current && locationState?.activeQuestion?.studentNote) {
					hasStartedRef.current = true;
					setTimeout(() => {
						setShowQuestionNoteModal(true);
					}, 1000);
				}
			}
		} catch (error) {
			console.log('Recording error :', error);
		}
	}, [getRecorder, locationState?.activeQuestion?.studentNote]);

	const stopRecording = useCallback(
		async (dontOpenSolutionVideoModal = false) => {
			const recorder = getRecorder();
			try {
				await recorder.stopRecording();
				if (!dontOpenSolutionVideoModal) {
					setTimeout(() => {
						setShowSolutionVideoModal(true);
					}, 100);
				}
			} catch (error) {
				console.error('Error stopping recording:', error);
			}
		},
		[getRecorder]
	);

	const togglePause = useCallback(() => {
		const recorder = getRecorder();
		try {
			if (recordingState.isPaused) {
				recorder.resumeRecording();
			} else {
				recorder.pauseRecording();
			}
		} catch (error) {
			console.error('Error toggling pause:', error);
		}
	}, [recordingState.isPaused, getRecorder]);

	const handleDeleteVideo = async (data: PassFormData) => {
		if (recordingState.isRecording) {
			stopRecording(true);
		}

		await createPassedQuestion.mutateAsync({
			questionId: locationState?.activeQuestion?.questionId || '',
			passReasonId: parseInt(data.passQuestion)
		});
		solverTimer.clearTimer();
		reset();

		setTimeout(() => {
			setShowDeleteVideoModal(false);
			navigate(NavigationPath.solverSolve);
		}, 100);
	};

	const handleRetry = () => {
		setShowSolutionVideoModal(false);
		setRecordingState({
			isRecording: false,
			isPaused: false,
			currentTime: 0,
			recordedData: undefined
		});
		hasStartedRef.current = false;

		recorderRef.current?.destroy();
		recorderRef.current = null;

		sendMessageToWebView({
			type: 'SHOW_NATIVE_BUTTONS',
			data: {}
		});
	};

	const handleCloseWhiteBoard = async () => {
		setShowDeleteVideoModal(true);
	};

	const handleCloseDeleteModal = () => {
		reset();
		setShowDeleteVideoModal(false);
	};

	const handleVideoSubmitSuccess = () => {
		solverTimer.clearTimer();
		navigate(NavigationPath.solverSolve);
	};

	const handleVideoSubmitError = () => {
		toast.error('Video gönderilirken bir hata oluştu!');
	};

	const onPressQuestionImageToWhiteboard = () => {
		setShowQuestionImageOnWhiteBoard(true);
		whiteboardRef?.current?.questionImageToWhiteboard(`${locationState?.activeQuestion?.questionImage}?v=${Date.now()}`);
	};

	const handleClearClick = () => {
		setShowQuestionImageOnWhiteBoard(false);
	};

	const handleTimeout = () => {
		navigate(NavigationPath.solverSolve);
	};

	return (
		<Fragment>
			<div className="white-board-container">
				{!isWebView() && (
					<Fragment>
						<button
							className="close-white-board-button"
							onClick={handleCloseWhiteBoard}>
							<i className="icons-x" />
						</button>

						{!showSolutionVideoModal && !locationState?.isResolveMode && solverTimer.isActive && (
							<div className="remaining-time-wrapper">
								<p>
									Kalan Süre:
									<b
										style={{
											color: solverTimer.remainingTime <= WARNING_TIME_THRESHOLD ? 'var(--danger-text)' : 'inherit'
										}}>
										{formatTime(solverTimer.remainingTime)}
									</b>
								</p>
							</div>
						)}
					</Fragment>
				)}

				{!showQuestionImageOnWhiteBoard && (
					<div className="question-image-wrapper">
						<img
							src={locationState?.activeQuestion?.questionImage}
							alt="Soru görseli"
						/>
						<button
							className="add-to-white-board-button"
							onClick={onPressQuestionImageToWhiteboard}>
							Soruyu Tahtaya Ekle
						</button>
					</div>
				)}

				{showQuestionInfo && (
					<div className="question-info-wrapper">
						<div className="question-info-header">
							<div className="question-info-header-title-wrapper">
								<span>{locationState?.activeQuestion?.curriculumName}</span>
								<h6>{locationState?.activeQuestion?.curriculumElementUnitName}</h6>
							</div>
							<button
								type="button"
								className="question-info-header-close-button"
								onClick={() => setShowQuestionInfo(false)}>
								<i className="icons-x" />
							</button>
						</div>
						<div className="question-info-content">
							<div className="question-info-content-answer-wrapper">
								<p>
									Cevap anahtarı: <strong>{locationState?.activeQuestion?.trueAnswerKey}</strong>
								</p>
							</div>
							{locationState?.activeQuestion?.studentNote && (
								<div className="question-info-content-question-description">
									<p>
										{truncateText(locationState?.activeQuestion?.studentNote)}
										{locationState?.activeQuestion?.studentNote?.length >= NOTE_TRUNCATE_LENGTH && (
											<button
												className="question-info-content-see-more-button"
												onClick={() => setShowQuestionNoteModal(true)}>
												Devamını Gör
											</button>
										)}
									</p>
								</div>
							)}
						</div>
					</div>
				)}

				{!isWebView() && (
					<div className="record-action-buttons">
						{!recordingState.isRecording && !recordingState.recordedData && (
							<button
								className="record-action-button"
								onClick={startRecording}
								disabled={!locationState?.isResolveMode && !solverTimer.isActive}>
								<div className="retry-icon" />
								Kaydı Başlat
							</button>
						)}

						{recordingState.isRecording && !recordingState.isPaused && (
							<>
								<button
									className="record-action-button"
									onClick={togglePause}>
									<div className="icon-bg">
										<i className="icons-pause" />
									</div>
									Kaydı Duraklat
								</button>
								<button
									className="record-action-button"
									onClick={() => stopRecording()}>
									<div className="stop-icon" />
									Kaydı Sonlandır
								</button>
							</>
						)}

						{recordingState.isRecording && recordingState.isPaused && (
							<>
								<div className="continue-record-button-time-wrapper">
									<div className="video-record-time">
										<div className="record-icon" />
										{formatTime(recordingState.currentTime)}
									</div>
									<button
										className="record-action-button"
										onClick={togglePause}>
										<i className="icons-play-circle" />
										Kayda Devam Et
									</button>
								</div>
								<button
									className="record-action-button"
									onClick={() => stopRecording()}>
									<div className="stop-icon" />
									Kaydı Sonlandır
								</button>
							</>
						)}

						{!showQuestionInfo && (
							<button
								className="record-action-button"
								onClick={() => setShowQuestionInfo(true)}>
								<i className="icons-note" />
								Soru Notu
							</button>
						)}
					</div>
				)}
			</div>

			<WhiteBoardWv
				ref={whiteboardRef}
				streamId={`solver-${interactionId}`}
				whiteboardLastData={[]}
				onWhiteBoardData={() => null}
				onClearClick={handleClearClick}
				isSolver
			/>

			<Modal
				isOpen={showQuestionNoteModal}
				onClose={() => setShowQuestionNoteModal(false)}
				contentClassName="question-note-modal"
				showCloseButton
				size="md"
				variant="centered"
				customXIcon={<i className="icons-x" />}>
				<div className="modal-header">
					<div className="modal-title">
						<span>{locationState?.activeQuestion?.curriculumName}</span>
						<h2>{locationState?.activeQuestion?.curriculumElementUnitName}</h2>
					</div>
				</div>
				<div className="modal-body">
					<div className="answer-key">
						<p>
							Cevap anahtarı: <strong>{locationState?.activeQuestion?.trueAnswerKey}</strong>
						</p>
					</div>
					{locationState?.activeQuestion?.studentNote && (
						<div className="question-description">
							<p>{locationState?.activeQuestion?.studentNote}</p>
						</div>
					)}
				</div>
			</Modal>

			{isActiveSolverV2UploadScreen ? (
				<SolutionVideoModalV2
					isOpen={showSolutionVideoModal}
					onClose={() => {
						setShowSolutionVideoModal(false);
					}}
					onRetry={handleRetry}
					recordedVideoData={recordingState.recordedData}
					activeQuestion={locationState?.activeQuestion}
					solverTimer={solverTimer}
					onSuccess={handleVideoSubmitSuccess}
					onError={handleVideoSubmitError}
					showRetryButton={true}
					isResolveMode={locationState?.isResolveMode}
					resolveQueueId={locationState?.resolveQueueId}
				/>
			) : (
				<SolutionVideoModal
					isOpen={showSolutionVideoModal}
					onClose={() => {
						setShowSolutionVideoModal(false);
					}}
					onRetry={handleRetry}
					recordedVideoData={recordingState.recordedData}
					activeQuestion={locationState?.activeQuestion}
					solverTimer={solverTimer}
					onSuccess={handleVideoSubmitSuccess}
					onError={handleVideoSubmitError}
					showRetryButton={true}
					isResolveMode={locationState?.isResolveMode}
					resolveQueueId={locationState?.resolveQueueId}
				/>
			)}

			<DialogModal
				isOpen={showDeleteVideoModal}
				onClose={handleCloseDeleteModal}
				className="solver-dialog-modal"
				mobileBottomSheet
				variant="danger"
				title="Soruyu İptal Et"
				primaryButtonText="İptal Et"
				secondaryButtonText="Vazgeç"
				primaryButtonActive={isValid || createPassedQuestion.isLoading}
				onSecondaryClick={handleCloseDeleteModal}
				onPrimaryClick={handleSubmit(handleDeleteVideo)}
				customXIcon={<i className="icons-x" />}>
				<p dangerouslySetInnerHTML={purify('Soruyu iptal etmeniz halinde <b>1 pas hakkınız kullanılacaktır.</b> İptal işlemine devam edebilmek için lütfen iptal etme sebebini seçiniz.')} />
				<form className="report-form">
					{solverPassQuestion?.map((option, index) => (
						<div
							className="report-option"
							key={index}>
							<label className="report-option-label">
								<input
									type="radio"
									value={option?.id}
									{...register('passQuestion')}
									className="report-option-input"
								/>
								<span className="report-option-text">{option?.description}</span>
							</label>
						</div>
					))}
					{errors.passQuestion && <p className="error-message">{errors.passQuestion.message}</p>}
				</form>
			</DialogModal>

			<DialogModal
				isOpen={showTimeoutModal}
				onClose={handleCloseDeleteModal}
				className="solver-timeout-modal"
				showCloseIcon={false}
				variant="info"
				title="Süre Doldu!"
				primaryButtonText="Anladım, Anasayfaya Dön"
				onPrimaryClick={handleTimeout}
				customXIcon={<i className="icons-x" />}>
				<p dangerouslySetInnerHTML={purify('Belirlenen süre içinde videoyu göndermediğin için 1 kredi düşüldü. Ana sayfaya dönerek yeni bir soru seçebilirsin.')} />
			</DialogModal>
		</Fragment>
	);
};

export default SolverWhiteBoard;
